import type { ScopedChannel } from '@/types/auth'
import type { ChannelName } from '@/types/channel'

/**
 * Texto ÚNICO do estado "tenho um `channel_id` e não consigo resolver o nome dele".
 *
 * Substitui três strings que descreviam o MESMO estado em telas diferentes:
 * `"Restrito"` (badge da lista de contatos), `'Unknown Channel'` (histórico do modal) e
 * `'Canal Desconhecido'` (painel do chat).
 *
 * NÃO confundir com `'canal removido'` (`lib/format/transfer.ts`) nem com `'Canal removido'`
 * (`components/notifications/renderers/protocol_moved.tsx`): aqueles são snapshot NULO de
 * transferência — o canal já não existia quando a entrada foi gravada. Estado diferente,
 * texto diferente, e é assim que continua.
 */
export const UNKNOWN_CHANNEL_LABEL = 'Canal desconhecido'

/** Qualquer lista que carregue identidade de canal (roster, escopada, redirects). */
type ChannelNameLike = { db_id: string; name: string }

/**
 * Une o roster (`GET /whatsapp/channels/names`, tenant-wide) com os canais escopados do
 * `/auth/me` — que o `AuthProvider` hidrata SÍNCRONO do localStorage e por isso sobrevive
 * a roster lento, 403 ou fora do ar.
 *
 * Molde de `mergeBrandChannels` (`components/layout/AppRail/brandChannels.ts`, fix 3064e516),
 * com a base INVERTIDA: lá a identidade vem do auth e a HTTP só enriquece; aqui o roster É a
 * identidade (é o superconjunto, e já vem ordenado por nome pelo back — preservar essa ordem
 * é o que faz a lista de destinos sair alfabética sem o front reordenar).
 *
 * `roster` ausente NUNCA apaga a lista: degrada pro escopado, que é exatamente o
 * comportamento de hoje. Canal só no escopo (grant novo que o roster ainda não refletiu)
 * entra defensivamente no fim.
 */
export function mergeChannelRoster(
  roster: readonly ChannelName[] | null | undefined,
  scoped: readonly ScopedChannel[] | null | undefined,
): ChannelName[] {
  const base = roster ?? []
  const known = new Set(base.map((channel) => channel.db_id))
  const onlyScoped = (scoped ?? [])
    .filter((channel) => !known.has(channel.db_id))
    .map(({ db_id, name }) => ({ db_id, name }))

  return [...base, ...onlyScoped]
}

/**
 * Nome do canal no roster. `null` = NÃO RESOLVIDO (id fora do roster, roster ainda não
 * respondeu, ou nome vazio) — ignorância, não rótulo. Quem transforma ignorância em texto
 * é `channelDisplayName`, no limite da renderização.
 *
 * Nome vazio conta como não resolvido de propósito: o back declara `name: string | None`
 * e o service normaliza pra `''`; devolver `''` aqui renderizaria um badge em branco.
 */
export function findChannelName(
  channelId: string | null | undefined,
  roster: readonly ChannelNameLike[] | null | undefined,
): string | null {
  if (!channelId) return null
  const name = roster?.find((channel) => channel.db_id === channelId)?.name
  return name ? name : null
}

/** Nome pra EXIBIÇÃO: cai em `UNKNOWN_CHANNEL_LABEL` quando não resolve. */
export function channelDisplayName(
  channelId: string | null | undefined,
  roster: readonly ChannelNameLike[] | null | undefined,
): string {
  return findChannelName(channelId, roster) ?? UNKNOWN_CHANNEL_LABEL
}
